<template>
    <div class="tasks-page">
        <Header ref="header" helpURL="studio/tasks/">
            <template #page-slot>            
                Tasks
            </template>
        </Header>
        <div class="main-page-div">
            <main class="main-display-area">
                <!-- Filter Bar -->
                <div class="filter-bar">
                    <div class="help-tip-container">
                        <HelpTip> Only 20 tasks are shown at a time. Use the filters to find specific tasks.  
                        </HelpTip>
                    </div>
                    <div class="filter-bar-actions">
                        <PageControl
                            v-if="totalPages > 1"
                            v-model="currentPage"
                            :totalPages="totalPages"
                        />
                        <button
                            class="button-light"
                            :class="{ highlight: showFilterPanel }"
                            @click="showFilterPanel = !showFilterPanel"
                            title="Toggle Filter Panel"
                        >
                            <v-icon start>mdi-filter</v-icon>
                            Filter
                            <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
                        </button>
                        <div class="view-toggle">
                            <button 
                                class="view-toggle-btn"
                                :class="{ active: viewMode }"
                                @click="viewMode = viewMode === 'card' ? 'list' : 'card'"
                                :title="viewMode === 'card' ? 'Switch to List View' : 'Switch to Card View'"
                            >
                                <v-icon>{{ viewMode === 'card' ? 'mdi-view-list' : 'mdi-view-grid' }}</v-icon>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="loading" class="empty-state">
                    <div class="loading">Loading tasks...</div>
                </div>
                <template v-else>
                    <div v-if="tasks && tasks.length > 0" class="tasks-container">
                        <div class="tasks-list scrollbar" :class="{ 'list-mode': viewMode === 'list' }">
                            <TaskCard 
                                v-for="(task, index) in pagedTasks" 
                                :key="task.id || task.name || index"
                                :session="task"
                                :permissions="{ trainer_write: true }"
                                :is-list="viewMode === 'list'"
                                @delete-session="handleTaskDeleted"
                            />
                            <div v-if="pagedTasks.length === 0" class="empty-message">
                                No tasks found
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-state">
                        No tasks found
                    </div>
                </template>

                <div
                    ref="filterPanelEl"
                    :class="['filter-panel', { 'filter-panel--open': showFilterPanel }]"
                >
                    <div class="filter-panel__header">
                        <h3>Filters</h3>
                        <div class="filter-panel__actions">
                            <button
                                type="button"
                                class="clear-filters-btn"
                                :disabled="activeFilterCount === 0"
                                @click="clearAllFilters"
                            >
                                Clear All
                            </button>
                            <button class="filter-panel__close" @click="showFilterPanel = false">
                                <v-icon>mdi-close</v-icon>
                            </button>
                        </div>
                    </div>
                    <div class="filter-panel__content">
                        <TasksFilter
                            v-model="taskFilters"
                            :status-list="statusList"
                            :users-list="usersList"
                            :types-list="typesList"
                            :manage-type-list="manageTypeList"
                        />
                    </div>
                </div>
            </main>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import Header from '@/components/Header.vue';
import TaskCard from '@/components/Apps/TaskCard.vue';
import TasksFilter from '@/components/Apps/TasksFilter.vue';
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import HelpTip from '@/components/HelpTip.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

const $toast = useToast();
const store = useStore();
const currentUsername = computed(() => store.state?.user?.user?.username || '');
const dataConnect = new DatastoreConnect();

const tasks = ref([]);
const loading = ref(false);
const error = ref(null);
const viewMode = ref('card');
const showFilterPanel = ref(false);
const dynamicFilters = ref([]);
const taskFilters = ref({
    startDate: '',
    endDate: '',
    showDeleted: false,
    dynamicFilters: {},
});

const TASK_STATUS_OPTIONS = [
    { key: 'Queued', value: 'queued' },
    { key: 'Pending', value: 'pending' },
    { key: 'Initializing', value: 'init' },
    { key: 'Running', value: 'running' },
    { key: 'Complete', value: 'complete' },
    { key: 'Terminating', value: 'terminating' },
    { key: 'Terminated', value: 'terminated' },
    { key: 'Error', value: 'error' },
    { key: 'Deleted', value: 'deleted' },
];

const MANAGE_TYPE_OPTIONS = [
    { key: 'AWS', value: 'aws' },
    { key: 'AWS Batch', value: 'aws_batch' },
    { key: 'User', value: 'user' },
    { key: 'Kubernetes', value: 'kubernetes' },
];

const findTaskFilter = (matchers) => (dynamicFilters.value || []).find((filter) => {
    const filterName = String(filter.name || '').toLowerCase();
    const filterLabel = String(filter.label || '').toLowerCase();
    return matchers.some((matcher) => filterName === matcher || filterLabel === matcher);
});

const usersFilter = computed(() => findTaskFilter(['user', 'users', 'username']));
const typesFilter = computed(() => findTaskFilter(['type', 'types']));

const statusList = computed(() => TASK_STATUS_OPTIONS);
const usersList = computed(() => usersFilter.value?.options || []);
const typesList = computed(() => typesFilter.value?.options || []);
const manageTypeList = computed(() => MANAGE_TYPE_OPTIONS);

let defaultUserApplied = false;
const taskInfoSocket = ref(null);
let reconnectTimeout = null;
let isIntentionalSocketClose = false;

const getTaskInfoWebSocketEndpoint = () => {
    return '/ws/task_info';
};

const getTaskInfoWebSocketUrl = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const token = store.state?.user?.token;
    const baseUrl = `${protocol}://${window.location.host}${getTaskInfoWebSocketEndpoint()}`;

    // Keep same-origin cookie auth and also include query token for backends that accept it.
    if (token) {
        return `${baseUrl}?auth_token=${encodeURIComponent(token)}`;
    }

    return baseUrl;
};

const getSubscribedTaskIds = () => {
    return tasks.value
        .map((task) => Number(task?.id))
        .filter((taskId) => Number.isInteger(taskId) && taskId > 0);
};

const clearReconnectTimeout = () => {
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
};

const scheduleTaskInfoReconnect = () => {
    clearReconnectTimeout();
    reconnectTimeout = setTimeout(() => {
        connectTaskInfoSocket();
    }, 10000);
};

const extractTaskInfoUpdates = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.tasks)) {
        return payload.tasks;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (payload?.task && typeof payload.task === 'object') {
        return [payload.task];
    }

    return [];
};

const applyTaskInfoUpdates = (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) {
        return;
    }

    const updatesById = new Map(
        updates
            .map((update) => [Number(update?.id), update])
            .filter(([taskId]) => Number.isInteger(taskId) && taskId > 0)
    );

    if (updatesById.size === 0) {
        return;
    }

    tasks.value = tasks.value.map((task) => {
        const taskId = Number(task?.id);
        const update = updatesById.get(taskId);
        if (!update) {
            return task;
        }

        return {
            ...task,
            name: update.name ?? task.name,
            status: update.status ?? task.status,
            message: update.message ?? task.message,
            status_process_info: update.status_process_info ?? task.status_process_info,
            cloud_instance_id: update?.status_process_info?.id ?? task.cloud_instance_id,
        };
    });
};

const sendTaskInfoSubscription = (taskIds = getSubscribedTaskIds()) => {
    if (!taskInfoSocket.value || taskInfoSocket.value.readyState !== WebSocket.OPEN) {
        return;
    }

    if (!taskIds.length) {
        return;
    }

    taskInfoSocket.value.send(JSON.stringify({ task_ids: taskIds }));
};

const disconnectTaskInfoSocket = () => {
    clearReconnectTimeout();
    isIntentionalSocketClose = true;

    if (taskInfoSocket.value) {
        if (taskInfoSocket.value.readyState === WebSocket.OPEN || taskInfoSocket.value.readyState === WebSocket.CONNECTING) {
            taskInfoSocket.value.close();
        }
        taskInfoSocket.value = null;
    }
};

const connectTaskInfoSocket = () => {
    const taskIds = getSubscribedTaskIds();
    if (!taskIds.length) {
        disconnectTaskInfoSocket();
        return;
    }

    if (taskInfoSocket.value) {
        if (taskInfoSocket.value.readyState === WebSocket.OPEN) {
            sendTaskInfoSubscription(taskIds);
            return;
        }

        if (taskInfoSocket.value.readyState === WebSocket.CONNECTING) {
            return;
        }
    }

    clearReconnectTimeout();
    isIntentionalSocketClose = false;

    const wsUrl = getTaskInfoWebSocketUrl();
    const socket = new WebSocket(wsUrl);
    taskInfoSocket.value = socket;

    socket.onopen = () => {
        sendTaskInfoSubscription(taskIds);
    };

    socket.onmessage = (event) => {
        try {
            const payload = JSON.parse(event.data);

            if (payload?.message_type === 'error' || payload?.error) {
                console.error('Task info stream error:', payload?.message || payload?.error);
                return;
            }

            const updates = extractTaskInfoUpdates(payload);
            applyTaskInfoUpdates(updates);
        } catch (err) {
            console.error('Failed to parse task info frame:', err);
        }
    };

    socket.onclose = () => {
        taskInfoSocket.value = null;
        if (!isIntentionalSocketClose && getSubscribedTaskIds().length > 0) {
            scheduleTaskInfoReconnect();
        }
    };

    socket.onerror = () => {
        // onclose handles reconnect logic.
    };
};

const applyDefaultUserFilter = () => {
    if (defaultUserApplied) return;
    const username = currentUsername.value;
    if (!username) return;
    defaultUserApplied = true;
    taskFilters.value = {
        ...taskFilters.value,
        dynamicFilters: {
            ...(taskFilters.value.dynamicFilters || {}),
            username: [username],
        },
    };
};

// Add event listener on mount
onMounted(async () => {
    applyDefaultUserFilter();
    await loadTasks();

    document.addEventListener('mousedown', handleDocumentMouseDown);
});

// Remove event listener on unmount
onUnmounted(() => {
    disconnectTaskInfoSocket();
    document.removeEventListener('mousedown', handleDocumentMouseDown);
});

const filterPanelEl = ref(null);

function handleDocumentMouseDown(event) {
    if (!showFilterPanel.value) return;
    const panel = filterPanelEl.value;
    if (panel && panel.contains(event.target)) return;
    // Ignore clicks on the Filter toggle button or anything teleported (popper menus)
    const target = event.target;
    if (target.closest && (target.closest('.button-light') || target.closest('.popper-content') || target.closest('.v-overlay-container') || target.closest('.v-menu'))) {
        return;
    }
    showFilterPanel.value = false;
}

const selectedStatuses = computed(() => (taskFilters.value.dynamicFilters?.status || []).map((status) => String(status).toLowerCase()));
const selectedTaskName = computed(() => String(taskFilters.value.dynamicFilters?.name || '').trim().toLowerCase());

const taskMatchesSelectedStatus = (task) => {
    if (selectedStatuses.value.length === 0) {
        return true;
    }

    const statuses = [
        task?.status,
        task?.status_info?.status,
        task?.docker_task?.status,
    ].filter(Boolean).map((status) => String(status).toLowerCase());

    return selectedStatuses.value.some((selectedStatus) => statuses.some((status) => status.includes(selectedStatus)));
};

const visibleTasks = computed(() => {
    return [...tasks.value]
        .filter((task) => taskFilters.value.showDeleted || !isDeletedTask(task))
        .filter((task) => taskMatchesSelectedStatus(task))
        .filter((task) => {
            if (!selectedTaskName.value) {
                return true;
            }

            const taskName = String(task?.name || task?.task_name || task?.docker_task?.name || '').toLowerCase();
            return taskName.includes(selectedTaskName.value);
        });
});

const PAGE_SIZE = 10;
const currentPage = ref(1);
const totalTasks = ref(0);
const totalPages = computed(() => Math.max(1, Math.ceil(totalTasks.value / PAGE_SIZE)));
const pagedTasks = computed(() => visibleTasks.value);

watch(totalPages, (newTotal) => {
    if (currentPage.value > newTotal) {
        currentPage.value = newTotal;
    }
});

watch(currentPage, () => {
    loadTasks();
});

let lastFiltersKey = '';
watch(taskFilters, (value) => {
    const key = JSON.stringify({
        startDate: value.startDate || '',
        endDate: value.endDate || '',
        showDeleted: !!value.showDeleted,
        dynamicFilters: value.dynamicFilters || {},
    });
    if (key === lastFiltersKey) return;
    lastFiltersKey = key;
    if (currentPage.value !== 1) {
        currentPage.value = 1; // currentPage watcher will trigger loadTasks
    } else {
        loadTasks();
    }
}, { deep: true });


const activeFilterCount = computed(() => {
    let count = 0;

    if (taskFilters.value.startDate) count += 1;
    if (taskFilters.value.endDate) count += 1;
    if (taskFilters.value.showDeleted) count += 1;

    Object.values(taskFilters.value.dynamicFilters || {}).forEach((values) => {
        if ((Array.isArray(values) && values.length > 0) || (typeof values === 'string' && values.trim().length > 0)) {
            count += 1;
        }
    });

    return count;
});

const isDeletedTask = (task) => {
    const statuses = [
        task?.status,
        task?.status_info?.status,
        task?.docker_task?.status,
    ].filter(Boolean);

    return statuses.some((status) => String(status).toLowerCase().includes('deleted'));
};

async function loadTasks(silent = false) {
    if (!silent) {
        loading.value = true;
    }
    error.value = null;
    try {
        // Build params object with only selected filters
        const params = {
            offset: (currentPage.value - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
        };

        const filters = {};

        if (taskFilters.value.startDate) {
            filters.start_date = taskFilters.value.startDate;
        }

        if (taskFilters.value.endDate) {
            filters.end_date = taskFilters.value.endDate;
        }

        const usersFilterValues = taskFilters.value.dynamicFilters?.username;
        if (usersFilterValues && usersFilterValues.length > 0) {
            filters[(usersFilter.value?.name || 'username').toLowerCase()] = usersFilterValues;
        }

        const typesFilterValues = taskFilters.value.dynamicFilters?.types;
        if (typesFilterValues && typesFilterValues.length > 0) {
            filters[(typesFilter.value?.name || 'types').toLowerCase()] = typesFilterValues;
        }

        const manageTypesFilterValues = taskFilters.value.dynamicFilters?.manage_types;
        if (manageTypesFilterValues && manageTypesFilterValues.length > 0) {
            filters.manage_types = manageTypesFilterValues;
        }

        const taskNameFilterValue = String(taskFilters.value.dynamicFilters?.name || '').trim();
        if (taskNameFilterValue) {
            filters.name = taskNameFilterValue;
        }

        if (taskFilters.value.showDeleted) {
            filters.deleted = true;
        }

        if (Object.keys(filters).length > 0) {
            params.filters = filters;
        }
        
        const response = await dataConnect.listTasks(params);
        if (response && response.result) {
            tasks.value = response.result.tasks || [];
            totalTasks.value = Number(response.result.tasks_count ?? tasks.value.length);

            // Extract and store filters from API response
            if (response.result.filters) {
                dynamicFilters.value = response.result.filters;
            }

            connectTaskInfoSocket();
        } else {
            tasks.value = [];
            totalTasks.value = 0;
            connectTaskInfoSocket();
        }
    } catch (err) {
        error.value = 'Failed to load tasks';
        $toast.error('Failed to load tasks');
        console.error('Error loading tasks:', err);
    } finally {
        if (!silent) {
            loading.value = false;
        }
    }
}

async function handleTaskDeleted(deletedTask) {
    $toast.success('Task deleted successfully');
    // Refresh the task list from backend
    await loadTasks();
}

function clearAllFilters() {
    const currentDynamic = taskFilters.value.dynamicFilters || {};
    const cleared = {};
    Object.keys(currentDynamic).forEach((key) => {
        const current = currentDynamic[key];
        if (Array.isArray(current)) {
            cleared[key] = [];
        } else if (typeof current === 'string') {
            cleared[key] = '';
        } else {
            cleared[key] = null;
        }
    });
    taskFilters.value = {
        startDate: '',
        endDate: '',
        showDeleted: false,
        dynamicFilters: cleared,
    };
}
</script>

<style lang="scss" scoped>
.tasks-page {
    width: 100%;
    @include full-vh;
    display: flex;
    flex-direction: column;
}

.main-page-div {
    width: 100%;
    flex: 1;
    min-height: 0;
}

.main-display-area {
    position: relative;
}

.filter-bar {
    display: flex;
    align-items: center;
    padding: 4px 24px;
    background: var(--gray-100);
    border-bottom: 1px solid var(--gray-200);
    gap: 24px;
}

.filter-button {
    text-transform: capitalize;
    border: 1px solid #d0d0d0;

    :deep(.v-btn__content) {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
    }
}

.filter-heading {
    font-size: 16px;
    font-weight: 600;
    color: var(--body-text-color);
    margin: 0;
    min-width: 60px;
}

.multi-select-wrapper {
    position: relative;
    min-width: 180px;
}

.multi-select-display {
    padding: 6px 10px;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    font-size: 14px;
    background: white;
    color: var(--body-text-color);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 32px;

    &:hover {
        border-color: var(--gray-300);
    }

    .selected-text {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dropdown-arrow {
        margin-left: 8px;
        font-size: 10px;
        color: var(--body-text-color-secondary);
    }
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
}

.checkbox-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background: var(--gray-100);
    }

    input[type="checkbox"] {
        margin-right: 8px;
        cursor: pointer;
    }

    span {
        font-size: 14px;
        color: var(--body-text-color);
    }
}

.dropdown-actions {
    display: flex;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid var(--gray-200);
    background: var(--gray-50);

    button {
        flex: 1;
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;

        &.btn-clear {
            background: var(--gray-100);
            color: var(--body-text-color);

            &:hover {
                background: var(--gray-200);
            }
        }

        &.btn-apply {
            background: var(--primary-color, #1976d2);
            color: white;

            &:hover {
                background: var(--primary-color-dark, #1565c0);
            }
        }
    }
}

.status-select {
    cursor: pointer;
}

.help-tip-container {
    align-self: flex-start;
}

.filter-bar-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    margin-left: 4px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    background: #e53935;
    color: #ffffff;
}

.filter-bar-actions :deep(.filter-button) {
    text-transform: capitalize;
    border: 1px solid #d0d0d0;
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    border-right: solid 1px rgba(0,0,0,0.5);
    max-width: fit-content;

    @include themify() {
        color: themed('body-text-color')
    }

    &:hover {
        background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    .v-btn__content {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
    }
}

.filter-btn-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
    background: var(--color-primary, #250e81);
    color: white;
}

.filter-panel {
    position: fixed;
    right: 0;
    top: 0;
    height: 100%;
    width: 0;
    background: white;
    border-left: 1px solid #e0e0e0;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    transition: width 0.3s ease-in-out;
    overflow-y: auto;
    z-index: 100;
    display: flex;
    flex-direction: column;

    &--open {
        width: 380px;
    }

    &__header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid #e0e0e0;
        flex-shrink: 0;

        h3 {
            margin: 0;
            font-size: 1.125rem;
            font-weight: 600;
        }
    }

    &__content {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 16px;
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
    }
}

.clear-filters-btn {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #999;
    border-radius: 4px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: #e8e8e8;
        border-color: #666;
    }

    &:active {
        opacity: 0.8;
    }

    &[disabled] {
        cursor: not-allowed;
        opacity: 0.5;
    }
}

.main-display-area {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tasks-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: 16px;
    gap: 16px;
}

.tasks-list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 16px;
    padding: 16px 0;
}

.empty-message {
    width: 100%;
    text-align: center;
    padding: 20px;
    color: var(--body-text-color-secondary);
    font-style: italic;
}

.empty-state {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    height: 100%;
}

.loading {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.view-toggle {
    display: flex;
    gap: 8px;
    margin-left: auto;
    align-items: center;
}

.view-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid #ccc;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    color: #333;

    &:hover {
        background: #f5f5f5;
        border-color: #999;
    }

    &.active {
        background: #0066cc;
        border-color: #0066cc;
        color: white;
    }
}

.list-mode {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 8px;
    padding: 8px 0;
}
</style>
